import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Inbox, Zap, UserCheck } from "lucide-react";
import { TESTIMONIALS, PIPELINE } from "@/data";
import { Reveal, SectionHead, Logo } from "@/components/primitives";
import { DemoLink } from "@/components/Booking";

const fmt = (n) => n.toLocaleString("en-US");

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const t = TESTIMONIALS[i];
  const go = (d) => setI((v) => (v + d + TESTIMONIALS.length) % TESTIMONIALS.length);
  return (
    <section id="customers" className="relative py-24 lg:py-32" data-testid="section-testimonials">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead eyebrow="Customer testimonials so far" title={<>Teams that went agentic <em className="italic font-light text-[var(--qd-accent)]">on day one</em></>} />
        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-8">
            <div className="relative rounded-3xl p-8 sm:p-12 text-white overflow-hidden grain min-h-[340px] flex flex-col" style={{ background: "linear-gradient(150deg,#5000ff,#2b0ea8 70%,#150a45)" }} data-testid="testimonial-card">
              <div className="absolute -right-20 -bottom-24 w-72 h-72 rounded-full border border-white/15" />
              <div className="absolute -right-10 -bottom-14 w-40 h-40 rounded-full bg-white/10 blur-xl" />
              <span className="font-display text-7xl leading-none text-white/30">“</span>
              <AnimatePresence mode="wait">
                <motion.blockquote key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }} className="flex-1">
                  <p className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="testimonial-quote">{t.quote}</p>
                  <footer className="mt-8 text-sm text-white/70">
                    <span className="font-semibold text-white">{t.role}</span> · {t.industry}, {t.region}
                  </footer>
                </motion.blockquote>
              </AnimatePresence>
              <div className="mt-8 flex items-center justify-between relative">
                <span className="font-mono text-sm text-white/70" data-testid="testimonial-counter">{String(i + 1).padStart(2, "0")}/{String(TESTIMONIALS.length).padStart(2, "0")}</span>
                <div className="flex gap-2">
                  <button onClick={() => go(-1)} className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-[var(--qd-accent)] transition-colors" aria-label="Previous" data-testid="testimonial-prev"><ArrowLeft className="w-4 h-4" /></button>
                  <button onClick={() => go(1)} className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-[var(--qd-accent)] transition-colors" aria-label="Next" data-testid="testimonial-next"><ArrowRight className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={0.1}>
            <div className="glass rounded-3xl p-8 h-full flex flex-col">
              <p className="eyebrow">What they praised</p>
              <AnimatePresence mode="wait">
                <motion.ul key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-5 space-y-3">
                  {t.praised.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm font-medium"><span className="w-6 h-6 rounded-full bg-[var(--qd-tint)] text-[var(--qd-accent)] flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>{p}</li>
                  ))}
                </motion.ul>
              </AnimatePresence>
              <dl className="mt-auto pt-8 grid grid-cols-2 gap-4 text-sm">
                <div><dt className="text-[var(--qd-muted)] text-xs">Industry</dt><dd className="font-semibold mt-1">{t.industry}</dd></div>
                <div><dt className="text-[var(--qd-muted)] text-xs">Region</dt><dd className="font-semibold mt-1">{t.region}</dd></div>
              </dl>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const PIPE_ICONS = [Inbox, Zap, UserCheck];

export const Agents = () => (
  <section id="agents" className="relative py-24 lg:py-32" data-testid="section-agents">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <SectionHead eyebrow="AI agents" title={<>AI agents that handle your volume, resolve, and <em className="italic font-light text-[var(--qd-accent)]">hand off seamlessly.</em></>} />
      <div className="mt-14 grid lg:grid-cols-3 gap-5 relative">
        {PIPELINE.map((p, i) => {
          const Icon = PIPE_ICONS[i];
          return (
            <Reveal key={p.n} delay={i * 0.15} className="relative">
              <article className="glass card-lift rounded-3xl p-7 h-full" data-testid={`pipeline-${p.title.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-2xl bg-[var(--qd-accent)] text-white flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                  <span className="font-mono text-xs text-[var(--qd-muted)]">{p.n}</span>
                </div>
                <p className="eyebrow mt-7">{p.title}</p>
                <h3 className="font-display text-2xl mt-2">{p.sub}</h3>
                <p className="text-sm text-[var(--qd-muted)] mt-3 leading-relaxed">{p.desc}</p>
                <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--qd-accent)]"><span className="live-dot" />{p.status}</p>
              </article>
              {i < 2 && <span className="hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg items-center justify-center text-[var(--qd-accent)]"><ArrowRight className="w-4 h-4" /></span>}
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const FinalCTA = () => {
  return (
    <section className="relative px-4 sm:px-6 pb-10" data-testid="section-final-cta">
      <Reveal className="mx-auto max-w-7xl">
        <div className="relative rounded-[2.5rem] overflow-hidden text-white grain px-8 py-20 sm:py-28 text-center" style={{ background: "linear-gradient(135deg,#5000ff 0%,#3a0fd0 50%,#150a45 100%)" }}>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full border border-white/10 orbit-ring" style={{ animationDuration: "50s" }}><span className="absolute top-1/2 -left-2 w-4 h-4 rounded-full bg-white shadow-[0_0_30px_white]" /></div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full border border-white/10 orbit-ring" style={{ animationDuration: "30s", animationDirection: "reverse" }}><span className="absolute -top-1.5 left-1/2 w-3 h-3 rounded-full bg-violet-200 shadow-[0_0_20px_#ddd6fe]" /></div>
          <h2 className="relative font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl mx-auto">Your first agent can be live <em className="italic font-light text-violet-200">before the end of the demo.</em></h2>
          <p className="relative mt-6 text-white/75 text-base md:text-lg">Twenty minutes, your real queue, no slides.</p>
          <DemoLink ctx={{ source: "final-cta" }} className="relative btn-primary !bg-white !text-[var(--qd-accent)] mt-10 text-base" testId="final-cta-button" />
          <Link to="/quantumdesk-vs-gorgias" className="relative mt-8 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/20 transition-colors" data-testid="final-cta-gorgias-teaser">
            Coming from Gorgias? See how we compare <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
};

export const Footer = () => (
  <footer className="relative py-10" data-testid="footer">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <Logo />
        <p className="text-sm text-[var(--qd-muted)]">© 2026 Quantum Desk · Agentic customer support</p>
        <div className="flex items-center gap-4">
          <Link to="/blog" className="text-sm font-semibold text-[var(--qd-ink)] hover:text-[var(--qd-accent)] transition-colors" data-testid="footer-blog-link">Blog</Link>
          <DemoLink ctx={{ source: "footer" }} className="link-arrow text-sm" testId="footer-book-demo" />
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1.5"><span className="live-dot" /> All systems operational</span>
        </div>
      </div>
      <nav aria-label="Legal and comparisons" className="mt-6 pt-6 border-t border-[var(--qd-line)] flex flex-wrap items-center justify-center sm:justify-start gap-x-6 gap-y-2 text-sm text-[var(--qd-muted)]" data-testid="footer-secondary-links">
        <Link to="/pricing" className="hover:text-[var(--qd-accent)] transition-colors" data-testid="footer-pricing">Resolution pricing</Link>
        <Link to="/quantumdesk-vs-gorgias" className="hover:text-[var(--qd-accent)] transition-colors" data-testid="footer-compare-gorgias">Compare with Gorgias</Link>
        <Link to="/terms-of-service" className="hover:text-[var(--qd-accent)] transition-colors" data-testid="footer-terms-of-service">Terms of Service</Link>
        <Link to="/privacy-policy" className="hover:text-[var(--qd-accent)] transition-colors" data-testid="footer-privacy-policy">Privacy Policy</Link>
        <a href="mailto:support@quantumdesk.com" className="hover:text-[var(--qd-accent)] transition-colors" data-testid="footer-email">support@quantumdesk.com</a>
      </nav>
      <div className="mt-5 flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-2" data-testid="footer-listings">
        <span className="text-xs font-semibold tracking-[0.14em] uppercase text-[var(--qd-muted)] mr-1">Listed on</span>
        {LISTINGS.map((l) => (
          <a key={l.name} href={l.href} target="_blank" rel="noopener" title={`Quantum Desk on ${l.name}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--qd-line)] px-3 py-1.5 text-xs font-semibold text-[var(--qd-ink)] hover:border-[var(--qd-accent)] hover:text-[var(--qd-accent)] transition-colors" data-testid={`footer-listing-${l.key}`}>
            <img src={l.logo} alt="" width={l.width} height="16" loading="lazy" className="h-4 w-auto" />
            <span className={l.wordmark ? "sr-only" : ""}>{l.name}</span>
          </a>
        ))}
      </div>
    </div>
  </footer>
);

const LISTINGS = [
  { key: "g2", name: "G2", href: "https://www.g2.com/products/quantumdesk/reviews", logo: "/badges/g2.svg", width: 16 },
  { key: "capterra", name: "Capterra", href: "https://www.capterra.com/p/10042272/QuantumDesk/", logo: "/badges/capterra.svg", width: 70, wordmark: true },
  { key: "sourceforge", name: "SourceForge", href: "https://sourceforge.net/software/product/QuantumDesk/", logo: "/badges/sourceforge.svg", width: 103, wordmark: true },
];
